import { apiSlice } from "../../app/api/apiSlice";

export const eventRegistrationsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    // ── ADMIN: Anmeldungsliste (paginiert, filterbar) ─────────────────────────
    // arg: { eventId, page, limit, status, paymentStatus, search }
    getEventRegistrations: builder.query({
      query: ({ eventId, ...params }) => ({
        url: `/events/${eventId}/registrations`,
        params,
      }),
      providesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `LIST-${eventId}` },
      ],
    }),

    // ── ADMIN: Sensible Gesundheitsdaten laden ───────────────────────────────
    getRegistrationHealthData: builder.query({
      query: ({ eventId, regId }) =>
        `/events/${eventId}/registrations/${regId}/health`,
    }),

    // ── ADMIN: Manuelle Buchung anlegen ──────────────────────────────────────
    createManualRegistration: builder.mutation({
      query: ({ eventId, ...body }) => ({
        url: `/events/${eventId}/registrations/manual`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `LIST-${eventId}` },
      ],
    }),

    // ── ADMIN: Status ändern ──────────────────────────────────────────────────
    updateRegistrationStatus: builder.mutation({
      query: ({ eventId, regId, ...body }) => ({
        url: `/events/${eventId}/registrations/${regId}/status`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `LIST-${eventId}` },
      ],
    }),

    // ── ADMIN: Zahlungsstatus ändern ──────────────────────────────────────────
    updatePaymentStatus: builder.mutation({
      query: ({ eventId, regId, ...body }) => ({
        url: `/events/${eventId}/registrations/${regId}/payment`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `LIST-${eventId}` },
      ],
    }),

    // ── ADMIN: Anmeldung dauerhaft löschen ───────────────────────────────────
    deleteRegistration: builder.mutation({
      query: ({ eventId, regId }) => ({
        url: `/events/${eventId}/registrations/${regId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `LIST-${eventId}` },
      ],
    }),

    // ── ADMIN: Preis einer Buchung nachträglich ändern ─────────────────────────
    updateRegistrationPricing: builder.mutation({
      query: ({ eventId, registrationId, ...body }) => ({
        url: `/events/${eventId}/registrations/${registrationId}/pricing`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `LIST-${eventId}` },
      ],
    }),

    // ── ADMIN: SEPA-Export ───────────────────────────────────────────────────
    exportSepaRegistrations: builder.mutation({
      query: ({ eventId, ...body }) => ({
        url: `/events/${eventId}/registrations/export-sepa`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, { eventId, dryRun }) =>
        dryRun
          ? []
          : [{ type: "EventRegistration", id: `LIST-${eventId}` }],
    }),

    // ── ADMIN: Zahlungsstatus in Bulk ändern ─────────────────────────────────
    bulkUpdatePaymentStatus: builder.mutation({
      query: ({ eventId, ...body }) => ({
        url: `/events/${eventId}/registrations/payment/bulk`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `LIST-${eventId}` },
      ],
    }),

    // ── MEMBER: Event buchen ──────────────────────────────────────────────────
    bookEvent: builder.mutation({
      query: ({ eventId, ...body }) => ({
        url: `/events/${eventId}/book`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `MY-${eventId}` },
        { type: "EventRegistration", id: `LIST-${eventId}` },
        { type: "Event", id: eventId },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),

    // ── MEMBER: Eigene Buchung laden ──────────────────────────────────────────
    getMyRegistration: builder.query({
      query: (eventId) => `/events/${eventId}/my-registration`,
      providesTags: (_res, _err, eventId) => [
        { type: "EventRegistration", id: `MY-${eventId}` },
      ],
    }),

    // ── MEMBER: Buchung bearbeiten ────────────────────────────────────────────
    updateMyRegistration: builder.mutation({
      query: ({ eventId, ...body }) => ({
        url: `/events/${eventId}/my-registration`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `MY-${eventId}` },
        { type: "EventRegistration", id: `LIST-${eventId}` },
        { type: "Event", id: eventId },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),

    // ── MEMBER: Buchung stornieren ────────────────────────────────────────────
    cancelMyRegistration: builder.mutation({
      query: ({ eventId, reason }) => ({
        url: `/events/${eventId}/my-registration`,
        method: "DELETE",
        body: { reason },
      }),
      invalidatesTags: (_res, _err, { eventId }) => [
        { type: "EventRegistration", id: `MY-${eventId}` },
        { type: "EventRegistration", id: `LIST-${eventId}` },
        { type: "Event", id: eventId },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),
  }),
});

export const {
  useGetEventRegistrationsQuery,
  useLazyGetRegistrationHealthDataQuery,
  useCreateManualRegistrationMutation,
  useUpdateRegistrationStatusMutation,
  useUpdatePaymentStatusMutation,
  useDeleteRegistrationMutation,
  useUpdateRegistrationPricingMutation,
  useExportSepaRegistrationsMutation,
  useBulkUpdatePaymentStatusMutation,
  useBookEventMutation,
  useGetMyRegistrationQuery,
  useUpdateMyRegistrationMutation,
  useCancelMyRegistrationMutation,
} = eventRegistrationsApiSlice;
